import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DragAndDropUploader } from "../../../../../../../components/DragAndDropUploader";
import Button from "../../../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../../../components/button/types";
import { useMutation, useQuery } from "react-query";
import {
  deleteDefaultCvFile,
  deleteDefaultTemplate,
  fetchDefaultSettingsData,
  fetchSignedUrl,
  submitDefaultSettings,
} from "../../../../../../../services/selfAssessment.service";
import DeleteIcon from "@mui/icons-material/Delete";
import { DragAndDropUploaderModified } from "../../../../../../../components/DragAndDropUploader/DragAndDropModified";
import EditIcon from "@mui/icons-material/Edit";
import { MAX_CHARACTERS } from "../../../utils";

export interface DefaultSettingsData {
  cvFile: any;
  template: string;
}

const DefaultSettingsTab: React.FC<any> = () => {
  const { t } = useTranslation("myArea");
  const [fileError, setFileError] = useState<string | null>(null);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [template, setTemplate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t: tcommon } = useTranslation("common");

  const [isEditable, setIsEditable] = useState<boolean>(false);

  // Fetch default settings data
  const { data: defaultSettings, refetch } = useQuery<DefaultSettingsData>({
    queryKey: ["defaultSettings"],
    queryFn: fetchDefaultSettingsData,
  });

  // Function to reset fields
  const handleCancel = () => {
    setTemplate("");
    if (defaultSettings?.template) setTemplate(defaultSettings?.template);
    refetch();
    setIsEditable(false);
  };

  // Handle file delete

  const deleteMutation = useMutation({
    mutationFn: deleteDefaultCvFile,
    onSuccess: () => {
      refetch();
      setCvFile(null);
    },
    onError: () => {
      console.error("Failed to delete CV file");
    },
  });

  const handleDeleteCvFile = () => {
    deleteMutation.mutate();
  };

  const deleteTemplateMutation = useMutation({
    mutationFn: deleteDefaultTemplate,
    onSuccess: () => {
      setTemplate("");
      refetch();
    },
    onError: () => {
      console.error("Failed to delete template");
    },
  });

  const handleDeleteTemplate = () => {
    deleteTemplateMutation.mutate();
  };

  const handleDownload = async () => {
    try {
      const url = await fetchSignedUrl(defaultSettings?.cvFile?.awsKey);

      setTimeout(() => {
        window.open(url, "_blank");
      });
    } catch (error) {
      console.error("Error fetching signed URL:", error);
    }
  };

  // Mutation function
  const submitMutation = useMutation({
    mutationFn: ({
      cvFile,
      template,
    }: {
      cvFile: File | null;
      template: string;
    }) => submitDefaultSettings({ cvFile, template }),
  });

  const submitCvMutation = useMutation({
    mutationFn: ({ cvFile }: { cvFile: File | null }) =>
      submitDefaultSettings({ cvFile }),
  });

  const submitTemplateMutation = useMutation({
    mutationFn: ({ template }: { template: string }) =>
      submitDefaultSettings({ template }),
  });

  // Handle submit
  const handleSubmitCv = () => {
    setIsSubmitting(true);
    submitCvMutation.mutate(
      { cvFile },
      {
        onSuccess: () => {
          setIsSubmitting(false);
          setCvFile(null);
          refetch();
        },
        onError: () => {
          setIsSubmitting(false);
        },
      }
    );
  };

  const handleSubmitTemplate = () => {
    submitTemplateMutation.mutate(
      { template },
      {
        onSuccess: () => {
          setIsEditable(false);
          refetch();
        },
        onError: () => {},
      }
    );
  };

  useEffect(() => {
    if (cvFile) {
      handleSubmitCv();
    }
  }, [cvFile]);

  useEffect(() => {
    if (defaultSettings?.cvFile) {
      setCvFile(defaultSettings.cvFile);
    }
    if (defaultSettings?.template) {
      setTemplate(defaultSettings.template);
    }
  }, [defaultSettings]);

  const isOverLimit = template.length > MAX_CHARACTERS;

  return (
    <div className="mt-6 p-2">
      {/* CV File Section */}
      <div className="text-[16px] font-bold mb-2">
        {t("assessments.cover-letter.cover-letter-your-cv")}
      </div>
      <p>{t("assessments.cover-letter.cover-letter-your-cv-description")}</p>

      {/* Display existing CV file name if available */}
      {defaultSettings?.cvFile && (
        <div className="flex justify-between mt-8 mb-8 text-[#757575] font-bold cursor-pointer break-words overflow-wrap break-all">
          <span className="mr-6" onClick={handleDownload}>
            {defaultSettings.cvFile.name}
          </span>
          <DeleteIcon
            className="cursor-pointer "
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteCvFile();
            }}
          />
        </div>
      )}

      {isSubmitting && (
        <div className="flex justify-between mt-8 mb-8 text-[#757575] font-bold ">
          {t("assessments.cover-letter.pdf-submitting")}
        </div>
      )}

      {!(cvFile || defaultSettings?.cvFile) && (
        <>
          <DragAndDropUploaderModified
            singleFile
            filesSetter={(files) => {
              if (files.length > 0) {
                const file = files[0];

                if (file.size > 4 * 1024 * 1024) {
                  setFileError("File size exceeds 2MB");
                  setCvFile(null);
                } else {
                  setCvFile(file);
                  setFileError(null);
                }
              } else {
                setCvFile(null);
              }
            }}
            currentFile={cvFile}
          />
        </>
      )}
      {/* Template Input Section */}
      <div className="text-[16px] font-bold mb-2 mt-4">
        {t("assessments.cover-letter.cover-letter-template")}
      </div>
      <p>{t("assessments.cover-letter.cover-letter-template-description")}</p>

      {/* Display the existing template if available */}
      <div className="flex items-center justify-between">
        <textarea
          placeholder={
            t(
              "assessments.cover-letter.cover-letter-template-placeholder"
            ) as string
          }
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
          rows={8}
          className="mb-4 mt-4 border p-2 w-full resize-none rounded-md"
          readOnly={!isEditable && !!defaultSettings?.template}
        />
      </div>
      {isOverLimit && (
        <p className="text-red-900 text-sm">
          {tcommon("character-limit-error", {
            max: MAX_CHARACTERS,
          })}
        </p>
      )}
      {defaultSettings && defaultSettings.template && !isEditable && (
        <div className="flex justify-end text-[#757575] font-bold">
          <EditIcon
            className="cursor-pointer mr-4"
            onClick={(e) => {
              e.stopPropagation();
              setIsEditable(true);
            }}
          />
          <DeleteIcon
            className="cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteTemplate();
            }}
          />
        </div>
      )}
      {((!defaultSettings?.template && template) || isEditable) && (
        <>
          {/* Buttons */}
          <div className="flex justify-end space-x-2 mt-2">
            <Button
              variant={ButtonVariant.SECONDARY}
              size={ButtonSize.SMALL}
              onClick={handleCancel} // Cancel Button
            >
              {t("assessments.cover-letter.default-cancel")}
            </Button>
            <Button
              type="button"
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SMALL}
              onClick={handleSubmitTemplate}
              disabled={!template || isSubmitting || isOverLimit}
            >
              {t("assessments.cover-letter.default-save")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default DefaultSettingsTab;
